// Reusable form primitives for content editors in the admin
import React, { useState, useRef } from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Upload, Trash2, Plus, GripVertical, ImageIcon, Loader2, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { API, getToken } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { resolveImage } from '../../contexts/ContentContext';

export function Field({ label, children, hint }) {
  return (
    <div>
      {label && <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>}
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export function TextField({ value, onChange, placeholder, label, hint }) {
  return (
    <Field label={label} hint={hint}>
      <Input value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-11 rounded-xl border-sky-200" />
    </Field>
  );
}

export function TextAreaField({ value, onChange, placeholder, rows = 4, label, hint }) {
  return (
    <Field label={label} hint={hint}>
      <Textarea value={value ?? ''} rows={rows} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="rounded-xl border-sky-200 resize-none" />
    </Field>
  );
}

export function NumberField({ value, onChange, label, hint, step = 1 }) {
  return (
    <Field label={label} hint={hint}>
      <Input type="number" step={step} value={value ?? 0} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} className="h-11 rounded-xl border-sky-200" />
    </Field>
  );
}

export function SwitchField({ value, onChange, label, hint }) {
  return (
    <div className="flex items-center justify-between p-4 rounded-xl bg-sky-50/60 border border-sky-100">
      <div>
        <p className="font-semibold text-sky-950 text-sm">{label}</p>
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
      </div>
      <Switch checked={!!value} onCheckedChange={onChange} />
    </div>
  );
}

export function ImageField({ value, onChange, label = 'Image', hint }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const { toast } = useToast();

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/admin/upload`, formData, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
      toast({ title: 'Image téléversée' });
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Upload échoué', variant: 'destructive' });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Field label={label} hint={hint}>
      <div className="space-y-2">
        <div className="flex gap-2">
          <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="URL ou cliquez sur Téléverser" className="h-11 rounded-xl border-sky-200 text-sm" />
          <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} className="h-11 px-4 rounded-xl border-sky-200 flex-shrink-0">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          </Button>
          {value && (
            <Button type="button" variant="outline" onClick={() => onChange('')} className="h-11 px-4 rounded-xl border-sky-200 text-rose-600 flex-shrink-0">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          <input ref={inputRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
        </div>
        {value ? (
          <img src={resolveImage(value)} alt="Aperçu" className="w-full max-w-sm aspect-[16/9] object-cover rounded-xl border border-sky-100 bg-sky-50" />
        ) : (
          <div className="w-full max-w-sm aspect-[16/9] rounded-xl bg-sky-50/60 border-2 border-dashed border-sky-200 flex items-center justify-center text-slate-400">
            <ImageIcon className="w-8 h-8" />
          </div>
        )}
      </div>
    </Field>
  );
}

export function Repeater({ items = [], onChange, renderItem, newItem = () => ({}), label, addLabel = 'Ajouter' }) {
  const list = Array.isArray(items) ? items : [];
  const updateItem = (i, patch) => onChange(list.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const removeItem = (i) => { if (!window.confirm('Supprimer cet élément ?')) return; onChange(list.filter((_, idx) => idx !== i)); };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () => onChange([...list, newItem()]);
  return (
    <div>
      {label && <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-3 block">{label}</Label>}
      <div className="space-y-3">
        {list.map((item, i) => (
          <div key={i} className="relative bg-sky-50/40 rounded-2xl border border-sky-100 p-4">
            <div className="flex items-start gap-3">
              <div className="flex flex-col gap-1 pt-1">
                <button type="button" onClick={() => move(i, -1)} className="w-6 h-6 rounded-md hover:bg-sky-100 flex items-center justify-center text-slate-500" title="Monter">
                  <ArrowUp className="w-3 h-3" />
                </button>
                <span className="w-6 h-6 flex items-center justify-center text-[10px] font-bold text-slate-400"><GripVertical className="w-3 h-3" /></span>
                <button type="button" onClick={() => move(i, 1)} className="w-6 h-6 rounded-md hover:bg-sky-100 flex items-center justify-center text-slate-500" title="Descendre">
                  <ArrowDown className="w-3 h-3" />
                </button>
              </div>
              <div className="flex-1 space-y-3">
                {renderItem(item, (patch) => updateItem(i, patch), i)}
              </div>
              <button type="button" onClick={() => removeItem(i)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500 flex-shrink-0">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" onClick={add} className="w-full h-11 rounded-xl border-dashed border-sky-300 text-sky-700">
          <Plus className="w-4 h-4 mr-2" />{addLabel}
        </Button>
      </div>
    </div>
  );
}

export function SectionCard({ title, description, children }) {
  return (
    <div className="bg-white rounded-2xl border border-sky-100 p-6">
      {title && (
        <header className="mb-5 pb-4 border-b border-sky-50">
          <h3 className="font-display font-bold text-lg text-sky-950">{title}</h3>
          {description && <p className="text-xs text-slate-500 mt-1">{description}</p>}
        </header>
      )}
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export function SaveBar({ saving, onSave, onReset }) {
  return (
    <div className="sticky bottom-0 bg-white/90 backdrop-blur border-t border-sky-100 -mx-8 lg:-mx-10 px-8 lg:px-10 py-4 flex items-center justify-between gap-3 mt-8">
      <Button type="button" variant="ghost" onClick={onReset} className="text-slate-600 hover:text-rose-600">
        <RotateCcw className="w-4 h-4 mr-2" />Réinitialiser cette section
      </Button>
      <Button type="button" onClick={onSave} disabled={saving} className="h-11 px-7 bg-gradient-to-r from-sky-500 to-emerald-500 text-white font-bold rounded-xl border-0">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
      </Button>
    </div>
  );
}
